import { supabase } from '@/lib/supabase'
import type { AuthError, User, Session, AuthFlowType, Provider } from '@supabase/supabase-js'

export const authService = {
  // Reģistrācija
  /**
   * Sign up a user with email and password.
   * @param email - User's email
   * @param password - User's password
   * @param isOtpFlow - If true, use OTP code confirmation; if false, use link-based confirmation (default: true)
   */
  async signUp(email: string, password: string, isOtpFlow: boolean = true) {
    const options = isOtpFlow
      ? { emailConfirm: true, data: { role: 'owner' } }
      : { emailRedirectTo: import.meta.env.VITE_AUTH_REDIRECT_URL || `${window.location.origin}/auth/callback`, data: { role: 'owner' } };

    const { data, error } = await supabase.auth.signUp({ email, password, options });
    if (error) throw error;

    if (data.user && !data.user.email_confirmed_at) {
      return { ...data, message: 'Please check your email to confirm your account' };
    }

    return data;
  },

  // Pieteikšanās
  async signIn(email: string, password: string) {
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password,
    })
    if (error) throw error
    return data
  },

  // Izrakstīšanās
  async signOut() {
    const { error } = await supabase.auth.signOut()
    if (error) throw error
  },

  // Pašreizējā lietotāja iegūšana
  async getCurrentUser() {
    const { data: { user }, error } = await supabase.auth.getUser()
    if (error) throw error
    return user
  },

  // Sesijas pārvaldība
  // Session should be passed from useSession() context
  async getSessionFromContext(session) {
    if (!session) throw new Error('No session provided. Use useSession() hook.');
    return session;
  },

  // Paroles atjaunošana
  async resetPassword(email: string) {
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/auth/reset-password`,
    })
    if (error) throw error
  },

  // Add method to check email verification
  async checkEmailVerification() {
    // Always fetch fresh user data
    const { data: { user }, error } = await supabase.auth.getUser()
    if (error) throw error
    
    // Comprehensive check for all possible verification fields
    const isVerified = Boolean(
      user?.email_confirmed_at || 
      user?.confirmed_at || 
      // Google OAuth users are pre-verified
      user?.app_metadata.provider === 'google'
    )
    
    return isVerified
  },

  // Pieteikšanās ar Google
  async signInWithGoogle(): Promise<{ provider: Provider; url: string | null }> {
    try {
      const redirectTo =
        import.meta.env.MODE === 'development'
          ? 'http://localhost:5173/auth/callback'
          : 'https://app.onwheels.live/auth/callback';
  
      const { data, error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo,
          queryParams: {
            access_type: 'offline',
            prompt: 'consent'
          }
        }
      });
  
      if (error) throw error;
  
      return {
        provider: 'google',
        url: data?.url ?? null,
      };
    } catch (error) {
      console.error('Error during Google sign-in:', error);
      throw error;
    }
  }, // <-- add comma here
  async refreshUser() {
    const { data, error } = await supabase.auth.getUser();
    if (error) throw error;

    // ALSO fetch from your public.users table if needed
    // Don't throw if user doesn't exist in public.users - this is optional
    const { data: userRow, error: userRowError } = await supabase
      .from('users')
      .select('onboarding_completed')
      .eq('id', data.user.id)
      .maybeSingle();

    // Log but don't throw - user might not exist in public.users yet
    if (userRowError && userRowError.code !== 'PGRST116') {
      console.warn('Failed to fetch user from public.users table:', userRowError);
    }

    return {
      ...data.user,
      onboarding_completed:
        userRow?.onboarding_completed ??
        Boolean(data.user.user_metadata?.onboarding_completed) ??
        false
    };
  }
}
